import {
    Component, EventEmitter, Input, Output,
    OnInit, OnDestroy, ViewChildren, QueryList, ElementRef
} from '@angular/core';
import { FormArray, FormControl } from '@angular/forms';

function getFormArray(size: number): FormArray {
    const arr = [];
    for (let i = 0; i < size; i++) {
        arr.push(new FormControl(''));
    }
    return new FormArray(arr);
}

@Component({
    selector: 'app-verification-modal',
    templateUrl: './verification-modal.component.html',
    styleUrls: ['./verification-modal.component.css'],
    standalone: false
})
export class VerificationModalComponent implements OnInit, OnDestroy {
    @Input() isOpen = false;
    @Input() phoneNumber = '';
    @Input() resendDisabled = false;
    @Input() resendCountdown = 0;
    @Input() errorMessage = '';
    @Output() close = new EventEmitter<void>();
    @Output() submit = new EventEmitter<string>();
    @Output() resend = new EventEmitter<void>();

    @ViewChildren('inputEl') inputEls!: QueryList<ElementRef<HTMLInputElement>>;

    readonly size = 6;
    inputs = getFormArray(this.size);

    private scheduledFocus: number | null = null;

    ngOnInit(): void {
        if (this.isOpen) {
            setTimeout(() => this.focusInput(0), 100);
        }
    }

    ngOnDestroy(): void {
        this.resetCode();
    }

    // ── OTP input handlers (from proven pattern) ─────────────────

    handleFocus(e: FocusEvent): void {
        (e.target as HTMLInputElement).select();
    }

    handleKeyPress(e: KeyboardEvent, idx: number): boolean {
        const isDigit = /\d/.test(e.key);

        // Safari fires Cmd+V through keypress — let it through
        if (e.key === 'v' && e.metaKey) return true;

        if (isDigit && idx + 1 < this.size) {
            this.scheduledFocus = idx + 1;
        }

        if (isDigit && this.inputs.controls[idx].value) {
            // Clear existing value so it gets replaced, not appended
            this.inputs.controls[idx].setValue('');
        }

        return isDigit;
    }

    handleInput(): void {
        this.syncVerificationCode();

        if (this.scheduledFocus != null) {
            this.focusInput(this.scheduledFocus);
            this.scheduledFocus = null;
        }
    }

    handleKeyDown(e: KeyboardEvent, idx: number): void {
        if (e.key === 'Backspace' || e.key === 'Delete') {
            if (idx > 0) {
                this.scheduledFocus = idx - 1;
            }
        }
        if (e.key === 'ArrowLeft' && idx > 0) {
            e.preventDefault();
            this.focusInput(idx - 1);
        }
        if (e.key === 'ArrowRight' && idx < this.size - 1) {
            e.preventDefault();
            this.focusInput(idx + 1);
        }
    }

    handlePaste(e: ClipboardEvent, idx: number): void {
        e.preventDefault();
        const pasteData = e.clipboardData?.getData('text') ?? '';
        const digits = pasteData.replace(/\D/g, '').slice(0, this.size);

        if (!digits) return;

        digits.split('').forEach((d, i) => {
            this.inputs.controls[i]?.setValue(d);
        });

        // Clear remaining boxes
        for (let i = digits.length; i < this.size; i++) {
            this.inputs.controls[i]?.setValue('');
        }

        this.syncVerificationCode();
        this.focusInput(Math.min(digits.length, this.size - 1));
    }

    private focusInput(idx: number): void {
        // setTimeout prevents interfering with the current input event
        setTimeout(() => this.inputEls.get(idx)?.nativeElement.focus());
    }

    private syncVerificationCode(): void {
        setTimeout(() => {
            this.errorMessage = '';
        });
    }

    get verificationCodeValue(): string {
        return this.inputs.value.join('');
    }

    // ── modal actions ─────────────────────────────────────────────

    onSubmit(): void {
        const code = this.verificationCodeValue;
        if (code.length === this.size) {
            this.submit.emit(code);
        }
    }

    onResend(): void {
        if (!this.resendDisabled) this.resend.emit();
    }

    onClose(): void {
        this.resetCode();
        this.close.emit();
    }

    clearAndFocus(): void {
        this.resetCode();
        setTimeout(() => this.focusInput(0), 50);
    }

    resetCode(): void {
        this.inputs = getFormArray(this.size);
    }

    focusFirstInput(): void {
        this.focusInput(0);
    }

    get formattedCountdown(): string {
        const minutes = Math.floor(this.resendCountdown / 60);
        const seconds = this.resendCountdown % 60;
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }
}